import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import ComicCard from "@/components/base/ComicCard";
import FilterPanel from "@/pages/category/components/FilterPanel";
import { comics } from "@/mocks/comics";
import { getCategoryById } from "@/utils/manga";
import usePageMeta from "@/hooks/usePageMeta";

const PAGE_SIZE = 8;

export default function CategoryPage() {
  const [searchParams] = useSearchParams();
  const initialCat = searchParams.get("cat") || "all";

  const [category, setCategory] = useState(initialCat);
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState("hot");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const activeCategory = getCategoryById(category);
  const pageTitle = activeCategory
    ? `${activeCategory.name}漫画 - 韩漫大全无删减免费阅读下拉`
    : "漫画分类 - 韩漫大全无删减免费阅读下拉";
  const pageDesc = activeCategory
    ? `${activeCategory.description} 在韩漫大全免费无删减在线阅读${activeCategory.name}题材韩漫。`
    : "韩漫大全漫画分类页，按爱情、奇幻、悬疑、校园等题材筛选，支持状态与排序筛选，免费无删减在线阅读。";
  usePageMeta(pageTitle, pageDesc);

  useEffect(() => {
    setCategory(initialCat);
  }, [initialCat]);

  const filtered = useMemo(() => {
    const list = comics.filter((comic) => {
      if (category !== "all" && comic.category !== category) return false;
      if (status !== "all" && comic.status !== status) return false;
      return true;
    });
    return list.sort((a, b) => {
      if (sort === "update") return b.updatedAt.localeCompare(a.updatedAt);
      if (sort === "rating") return b.rating - a.rating;
      return b.views - a.views;
    });
  }, [category, status, sort]);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [category, status, sort]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisible((prev) =>
            prev < filtered.length ? prev + PAGE_SIZE : prev,
          );
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [filtered.length]);

  const handleReset = () => {
    setCategory("all");
    setStatus("all");
    setSort("hot");
    setDrawerOpen(false);
  };

  const visibleList = filtered.slice(0, visible);

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-8 py-6 md:py-10">
        <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-5 flex-wrap">
          <Link to="/" className="hover:text-accent-600">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/category" className="hover:text-accent-600">
            漫画分类
          </Link>
          {activeCategory && (
            <>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-950">{activeCategory.name}</span>
            </>
          )}
        </nav>

        <header className="mb-8">
          <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-950">
            {activeCategory ? `${activeCategory.name}漫画` : "全部漫画"}
          </h1>
          <p className="text-sm md:text-base text-foreground-600 mt-3 max-w-3xl leading-relaxed">
            {activeCategory
              ? activeCategory.description
              : "按题材、连载状态与排序方式自由筛选，快速定位你想看的韩漫作品，支持无删减免费下拉阅读。"}
          </p>
        </header>

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          <aside className="hidden lg:block w-64 shrink-0">
            <div className="sticky top-24 p-5 rounded-lg bg-background-100 border border-background-200">
              <FilterPanel
                category={category}
                status={status}
                sort={sort}
                onCategoryChange={setCategory}
                onStatusChange={setStatus}
                onSortChange={setSort}
                onReset={handleReset}
              />
            </div>
          </aside>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3 mb-5">
              <p className="text-sm text-foreground-600">
                共找到{" "}
                <span className="font-semibold text-accent-600">
                  {filtered.length}
                </span>{" "}
                部漫画
              </p>
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                className="lg:hidden inline-flex items-center gap-2 h-10 px-4 rounded-md border border-background-300 text-sm text-foreground-700 hover:bg-background-100 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-filter-3-line"></i>
                筛选
              </button>
            </div>

            {visibleList.length > 0 ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-5">
                  {visibleList.map((comic) => (
                    <ComicCard key={comic.id} comic={comic} />
                  ))}
                </div>
                <div ref={sentinelRef} className="h-4" />
                {visible < filtered.length && (
                  <div className="flex justify-center mt-8">
                    <button
                      type="button"
                      onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                      className="inline-flex items-center gap-2 h-11 px-7 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-add-line"></i>
                      加载更多
                    </button>
                  </div>
                )}
                {visible >= filtered.length && filtered.length > PAGE_SIZE && (
                  <p className="text-center text-xs text-foreground-400 mt-8">
                    已经到底啦，没有更多漫画了
                  </p>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 px-4 rounded-lg bg-background-100 border border-background-200 text-center">
                <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-foreground-400 text-3xl mb-4">
                  <i className="ri-emotion-sad-line"></i>
                </span>
                <h2 className="font-heading font-semibold text-lg text-foreground-950">
                  暂无符合条件的漫画
                </h2>
                <p className="text-sm text-foreground-500 mt-2 max-w-sm">
                  试试更换筛选条件，或重置后浏览全部漫画作品。
                </p>
                <button
                  type="button"
                  onClick={handleReset}
                  className="mt-5 inline-flex items-center gap-2 h-10 px-5 rounded-md bg-accent-500 text-white text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-refresh-line"></i>
                  重置筛选条件
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setDrawerOpen(false)}
          ></div>
          <div className="absolute right-0 top-0 h-full w-[85%] max-w-sm bg-background-50 p-5 overflow-y-auto animate-fade-in">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg text-foreground-950">
                筛选条件
              </h2>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-200 text-xl cursor-pointer"
                aria-label="关闭筛选"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
            <FilterPanel
              category={category}
              status={status}
              sort={sort}
              onCategoryChange={setCategory}
              onStatusChange={setStatus}
              onSortChange={setSort}
              onReset={handleReset}
            />
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="mt-6 w-full h-11 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer"
            >
              查看结果
            </button>
          </div>
        </div>
      )}
    </SiteLayout>
  );
}